import {
  EMPTY_STEP_METADATA,
  IWizardHouseState,
  T_WIZARD_HOUSE_STEP,
  TStepConfiguration,
  TWizardStepComponentMultiEditable,
  WIZARD_HOUSE_STEPS,
  WizardStepComponentType
} from "../interfaces";
import { WizardStepWelcome } from "./Steps/WizardStepWelcome";
import { WizardStepHouseLocation } from "./Steps/WizardStepHouseLocation";
import { WizardStepSpaceType } from "./Steps/WizardStepSpaceType";
import { WizardStepAboutHouseHold } from "./Steps/WizardStepAboutHousehold";
import { WizardStepAboutSpace } from "./Steps/WizardStepAboutSpace";
import { WizardStepFacilities } from "./Steps/WizardStepFacilities";
import { WizardStepUniqueness } from "./Steps/WizardStepUniqueness";
import { WizardStepPhotos } from "./Steps/WizardStepPhotos";
import { WizardStepPlanIntake } from "./Steps/WizardStepPlanIntake";
import { WizardStepAvailability } from "./Steps/WizardStepAvailability";
import { WizardStepPrices } from "./Steps/WizardStepPrices";
import { WizardStepIdealTenant } from "./Steps/WizardStepIdealTenant";
import { WizardStepActivityExpectations } from "./Steps/WizardStepActivityExpectations";
import { WizardStepFinishConfig } from "./Steps/WizardStepFinishConfig";
import { WizardStepWelcomePublish } from "./Steps/WizardStepWelcomePublish";
import {
  StepContainerNoBackground,
  StepContainerWide
} from "../Student/styles";
import { WizardStepOverview } from "../Student/Steps/WizardStepOverview";

export const WIZARD_HOUSE_DESCRIPTION_STEPS_ORDER: T_WIZARD_HOUSE_STEP[] = [
  WIZARD_HOUSE_STEPS.WelcomeDescription,
  WIZARD_HOUSE_STEPS.SpaceType,
  WIZARD_HOUSE_STEPS.HouseLocation,
  WIZARD_HOUSE_STEPS.AboutHousehold,
  WIZARD_HOUSE_STEPS.AboutSpace,
  WIZARD_HOUSE_STEPS.Facilities,
  WIZARD_HOUSE_STEPS.Uniqueness,
  WIZARD_HOUSE_STEPS.Photos,
  WIZARD_HOUSE_STEPS.DescriptionOverview
];
export const WIZARD_HOUSE_PUBLISH_STEPS_ORDER: T_WIZARD_HOUSE_STEP[] = [
  WIZARD_HOUSE_STEPS.WelcomePublish,
  WIZARD_HOUSE_STEPS.Availability,
  WIZARD_HOUSE_STEPS.Prices,
  WIZARD_HOUSE_STEPS.IdealTenant,
  WIZARD_HOUSE_STEPS.ActivityExpectations,
  WIZARD_HOUSE_STEPS.PlanedIntake,
  WIZARD_HOUSE_STEPS.PublishOverview,
  WIZARD_HOUSE_STEPS.FinishConfig
];

export const WIZARD_HOUSE_FULL_STEPS_ORDER: T_WIZARD_HOUSE_STEP[] = [
  WIZARD_HOUSE_STEPS.SpaceType,
  WIZARD_HOUSE_STEPS.HouseLocation,
  WIZARD_HOUSE_STEPS.AboutHousehold,
  WIZARD_HOUSE_STEPS.AboutSpace,
  WIZARD_HOUSE_STEPS.Facilities,
  WIZARD_HOUSE_STEPS.Uniqueness,
  WIZARD_HOUSE_STEPS.Photos,
  WIZARD_HOUSE_STEPS.Availability,
  WIZARD_HOUSE_STEPS.Prices,
  WIZARD_HOUSE_STEPS.IdealTenant,
  WIZARD_HOUSE_STEPS.ActivityExpectations,
  WIZARD_HOUSE_STEPS.FullOverview,
  WIZARD_HOUSE_STEPS.FinishConfig
];

export const WIZARD_HOUSE_DEFAULT_STATE: IWizardHouseState = {
  metadata: {
    started: false,
    finished: false,
    created_at: null,
    updated_at: null,
    most_recent_active_step: null
  },
  house_id: null,
  user_id: null,
  wizard_type: null,
  space_type: {
    metadata: EMPTY_STEP_METADATA,
    kind: "room"
  },
  house_location: {
    metadata: EMPTY_STEP_METADATA,
    additional_house_number: ""
  },
  about_household: {
    metadata: EMPTY_STEP_METADATA,
    people_living: null,
    characteristics: "single_person",
    pets: ["no_pets"],
    description: "",
    spoken_languages: ["GB", "NL"]
  },
  about_space: {
    metadata: EMPTY_STEP_METADATA,
    accommodation: "furnished",
    amount_of_people: "one",
    private_items: [],
    shared_items: ["toilet", "bathroom", "kitchen"]
  },
  facilities: {
    metadata: EMPTY_STEP_METADATA,
    security_items: ["smoke_detector", "fire_extinguisher"],
    other_items_and_services: ["wifi"]
  },
  uniqueness: {
    metadata: EMPTY_STEP_METADATA,
    accommodation: [],
    place_description: ""
  },
  photos: null,
  availability: {
    metadata: EMPTY_STEP_METADATA,
    available_from: null,
    available_to: null
  },
  prices: {
    metadata: EMPTY_STEP_METADATA,
    price: "0",
    deposit: "0",
    additional_expenses: "0",
    service_costs: "0"
  },
  ideal_tenant: {
    metadata: EMPTY_STEP_METADATA,
    tenant_description: [],
    tenant_spoken_languages: ["ANY"],
    tenant_characteristics: [],
    gender: "any",
    max_age: "60",
    min_age: "18"
  },
  activity_expectations: null,
  finish_config: {
    metadata: EMPTY_STEP_METADATA,
    host_verified: false,
    publish_house_after_finish: true
  }
};

export const WIZARD_HOUSE_DESCRIPTION_STEPS_CONFIGURATION: {
  [key in T_WIZARD_HOUSE_STEP]?: TStepConfiguration<
    IWizardHouseState,
    T_WIZARD_HOUSE_STEP
  >;
} = {
  [WIZARD_HOUSE_STEPS.WelcomeDescription]: {
    component: WizardStepWelcome,
    container: StepContainerWide,
    type: WizardStepComponentType.Static
  },
  [WIZARD_HOUSE_STEPS.SpaceType]: {
    component: WizardStepSpaceType,
    container: StepContainerWide,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.HouseLocation]: {
    component: WizardStepHouseLocation,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.AboutHousehold]: {
    component: WizardStepAboutHouseHold,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.AboutSpace]: {
    component: WizardStepAboutSpace,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.Facilities]: {
    component: WizardStepFacilities,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.Uniqueness]: {
    component: WizardStepUniqueness,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.Photos]: {
    component: WizardStepPhotos,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.PlanedIntake]: {
    component: WizardStepPlanIntake,
    type: WizardStepComponentType.Editable,
    showInOverview: false
  },
  [WIZARD_HOUSE_STEPS.Availability]: {
    component: WizardStepAvailability,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.Prices]: {
    component: WizardStepPrices,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.IdealTenant]: {
    component: WizardStepIdealTenant,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.ActivityExpectations]: {
    component: WizardStepActivityExpectations,
    type: WizardStepComponentType.Editable
  },
  [WIZARD_HOUSE_STEPS.DescriptionOverview]: {
    component: WizardStepOverview as TWizardStepComponentMultiEditable<
      IWizardHouseState,
      T_WIZARD_HOUSE_STEP
    >,
    type: WizardStepComponentType.MultiEditable
  },
  [WIZARD_HOUSE_STEPS.PublishOverview]: {
    component: WizardStepOverview as TWizardStepComponentMultiEditable<
      IWizardHouseState,
      T_WIZARD_HOUSE_STEP
    >,
    type: WizardStepComponentType.MultiEditable
  },
  [WIZARD_HOUSE_STEPS.FullOverview]: {
    component: WizardStepOverview as TWizardStepComponentMultiEditable<
      IWizardHouseState,
      T_WIZARD_HOUSE_STEP
    >,
    type: WizardStepComponentType.MultiEditable
  },
  [WIZARD_HOUSE_STEPS.FinishConfig]: {
    component: WizardStepFinishConfig,
    container: StepContainerNoBackground,
    type: WizardStepComponentType.Editable,
    showInOverview: false
  },
  [WIZARD_HOUSE_STEPS.WelcomePublish]: {
    component: WizardStepWelcomePublish,
    container: StepContainerWide,
    type: WizardStepComponentType.Static
  }
};
